/*
  Keyboard and screen-reader corrections for the PDP media gallery.

  Flickity drives this gallery from assets/z__jsProduct.js, a generated bundle the
  Asset API refuses to write, so its options cannot be changed. Two of its defaults
  are corrected from out here instead.

  1. Carousel-level tab stops.
     Flickity's `accessibility` option (default true) puts tabindex="0" on the
     carousel element itself so arrow keys scroll it. That makes three large
     role=generic divs focusable with no accessible name:

         div.product-gallery__main        617x617
         div.plyr                         617x1095   (Plyr adds its own)
         div.product-gallery__thumbnails  617x72

     A keyboard user tabs onto each in turn and a screen reader announces nothing,
     which is the same WCAG 4.1.2 defect already fixed on the thumbnails.

     The thumbnail strip and the Plyr wrapper lose the attribute outright; they
     carry no keyboard behaviour worth keeping. The main gallery keeps it, because
     Flickity answers arrow keys only while the carousel element itself holds
     focus, so stripping it leaves no arrow-key path to change the image at all.
     Instead it is named with the ARIA carousel pattern, which clears the finding
     without taking the behaviour away.

     Note the theme's visible Previous/Next buttons are separately unreachable:
     they are visibility:hidden by design. The arrow KEYS are a different path and
     do work, which is why the tab stop has to stay.

  2. aria-hidden on visible thumbnails.
     Flickity marks cells outside its current group aria-hidden="true" but leaves
     them with tabindex="0" and fully visible. That is aria-hidden-focus: the user
     can see the thumbnail and tab onto it, while its whole subtree is removed from
     the accessibility tree, so even a correct aria-label announces nothing. On an
     8-image product this silenced thumbnails 7 and 8.

  Flickity re-applies both on resize, on select and on re-init, so a one-shot pass
  at load is not enough. A MutationObserver scoped to .product-gallery re-asserts
  them. Same approach as z__jsVariantA11y.js, which exists for the same reason.
*/
(function () {
  'use strict';

  var ROOT = '.product-gallery';
  // Wrappers that must not be tab stops: they have no keyboard behaviour worth
  // keeping and no accessible name. Deliberately excludes .product-gallery__main,
  // which keeps its tab stop so Flickity's arrow keys still work, and
  // .product-gallery__thumbnail, which are named controls.
  var WRAPPERS = '.product-gallery__thumbnails, .plyr';
  var MAIN = '.product-gallery__main';
  var THUMB = '.product-gallery__thumbnail';

  var applying = false;

  function isVisible(el) {
    if (el.getClientRects().length === 0) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
  }

  function correct(root) {
    var i;
    var wrappers = root.querySelectorAll(WRAPPERS);
    for (i = 0; i < wrappers.length; i++) {
      if (wrappers[i].hasAttribute('tabindex')) {
        wrappers[i].removeAttribute('tabindex');
      }
    }

    // The main gallery keeps its tab stop: Flickity only answers arrow keys when
    // the carousel element itself holds focus, so removing it leaves no arrow-key
    // path to change the image. But a focusable div with no name is the 4.1.2
    // defect this file exists to fix, so it is named here with the ARIA carousel
    // pattern. Screen readers announce "Product gallery, N images, carousel".
    var main = root.querySelector(MAIN);
    if (main) {
      if (main.getAttribute('tabindex') !== '0') main.setAttribute('tabindex', '0');
      if (main.getAttribute('role') !== 'group') main.setAttribute('role', 'group');
      if (main.getAttribute('aria-roledescription') !== 'carousel') {
        main.setAttribute('aria-roledescription', 'carousel');
      }
      // data-media-count is rendered by snippets/product__images.liquid. Fall back
      // to counting slides if it is ever absent, and omit the count rather than
      // announce a wrong one.
      var count = parseInt(main.getAttribute('data-media-count'), 10);
      if (!count) count = main.querySelectorAll('.product-gallery__image').length;
      var label = count
        ? 'Product gallery, ' + count + (count === 1 ? ' image' : ' images')
        : 'Product gallery';
      if (main.getAttribute('aria-label') !== label) main.setAttribute('aria-label', label);
    }

    var thumbs = root.querySelectorAll(THUMB);
    for (i = 0; i < thumbs.length; i++) {
      // Only unhide what the user can actually see. If a thumbnail is genuinely
      // off screen Flickity may be right to hide it, and in that case it should
      // not be a tab stop either.
      if (thumbs[i].getAttribute('aria-hidden') === 'true') {
        if (isVisible(thumbs[i])) {
          thumbs[i].removeAttribute('aria-hidden');
        } else {
          thumbs[i].setAttribute('tabindex', '-1');
        }
      } else if (thumbs[i].getAttribute('tabindex') === '-1' && isVisible(thumbs[i])) {
        thumbs[i].setAttribute('tabindex', '0');
      }
    }
  }

  function run() {
    if (applying) return;
    applying = true;
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) correct(roots[i]);
    // Release on the next frame so the observer callbacks our own writes trigger
    // are swallowed rather than recursing.
    window.requestAnimationFrame(function () { applying = false; });
  }

  function observe() {
    var roots = document.querySelectorAll(ROOT);
    if (!roots.length) return false;
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-a11y-observed') === 'true') continue;
      roots[i].setAttribute('data-a11y-observed', 'true');
      new MutationObserver(run).observe(roots[i], {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex', 'aria-hidden'],
      });
    }
    run();
    return true;
  }

  function start() {
    if (observe()) return;
    // The gallery is rendered server side, but quick-shop and the image-scroll
    // template can mount one later. Watch for it, then stop watching.
    var wait = new MutationObserver(function () {
      if (observe()) wait.disconnect();
    });
    wait.observe(document.documentElement, { childList: true, subtree: true });
    // Never leave a document-wide observer running.
    window.setTimeout(function () { wait.disconnect(); }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Flickity finishes initialising after load and re-lays-out on resize; both
  // re-apply the attributes. The observer catches these, this is belt and braces
  // for the initial race.
  window.addEventListener('load', run);
})();
